import { useCallback, useEffect, useState } from 'react';
import { LOGIN_ROUTE } from '../routes';
import { markUserSessionAsActive } from './useTerminateInactiveUserSession.utils';
import useInterval from '@upheal/shared/libs/hooks/useInterval';

const LOGOUT_EVENT = 'logout-event';
const THERAPY_SESSION_TIMESTAMP = 'therapy-session-timestamp';
const THERAPY_SESSION_LOG_INTERVAL = 1000;

type SessionInProgressType = 'uphealVideoCall' | 'audioRecorder' | 'zoomApp';

interface SessionInProgressInfo {
    type: SessionInProgressType;
    id: string;
}

export const fireLogoutEvent = () => {
    localStorage.setItem(LOGOUT_EVENT, Math.random().toString());
};

export const addLogoutListener = () => {
    const handleStorageEvent = (event: StorageEvent) => {
        if (event.key === LOGOUT_EVENT) {
            window.onbeforeunload = null;
            window.location.href = LOGIN_ROUTE;
        }
    };

    window.addEventListener('storage', handleStorageEvent);
    const unsubscribe = () => {
        window.removeEventListener('storage', handleStorageEvent);
    };

    return unsubscribe;
};

type SessionInProgressLocalStorageValue = {
    type: SessionInProgressType;
    timestamp: number;
    id: string;
};

export const logTherapySessionInProgress = (
    sessionInfo: SessionInProgressInfo,
) => {
    const logTimestamp = () => {
        const sessionData: SessionInProgressLocalStorageValue = {
            timestamp: Date.now(),
            type: sessionInfo.type,
            id: sessionInfo.id,
        };
        localStorage.setItem(
            THERAPY_SESSION_TIMESTAMP,
            JSON.stringify(sessionData),
        );
        markUserSessionAsActive();
    };

    logTimestamp();

    const intervalId = setInterval(logTimestamp, THERAPY_SESSION_LOG_INTERVAL);

    const stopLogging = () => {
        clearInterval(intervalId);
        localStorage.removeItem(THERAPY_SESSION_TIMESTAMP);
    };

    return stopLogging;
};

export const isTherapySessionInProgress = (
    sessionInfo?: SessionInProgressInfo,
) => {
    const storedData = localStorage.getItem(THERAPY_SESSION_TIMESTAMP);

    if (!storedData) {
        return false;
    }

    try {
        const sessionData = JSON.parse(storedData) as
            | SessionInProgressLocalStorageValue
            | number; // Handle both new and old format
        const timestamp =
            typeof sessionData === 'object'
                ? sessionData.timestamp
                : sessionData;

        if (!timestamp) {
            return false;
        }

        const threshold = THERAPY_SESSION_LOG_INTERVAL + 100;
        const isActive = Date.now() - timestamp < threshold;

        // If checking for a specific session, verify it matches
        if (sessionInfo && isActive && typeof sessionData === 'object') {
            return (
                sessionData.type === sessionInfo.type &&
                sessionData.id === sessionInfo.id
            );
        }

        return isActive;
    } catch (error) {
        // Handle legacy format - just timestamp as string
        const timestamp = Number(storedData);
        if (!timestamp) {
            return false;
        }
        const threshold = THERAPY_SESSION_LOG_INTERVAL + 100;
        return Date.now() - timestamp < threshold;
    }
};

export const useIsTherapySessionInProgress = (
    sessionInfo?: SessionInProgressInfo,
) => {
    const [isInProgress, setIsInProgress] = useState<boolean>(() => {
        return isTherapySessionInProgress(sessionInfo);
    });

    const updateIsInProgress = useCallback(() => {
        setIsInProgress(isTherapySessionInProgress(sessionInfo));
    }, [sessionInfo]);

    useEffect(() => {
        window.addEventListener('storage', updateIsInProgress);

        return () => {
            window.removeEventListener('storage', updateIsInProgress);
        };
    }, [updateIsInProgress]);

    // When the active tab gets closed, storage events are not fired anymore, so we need to poll the value, not to get stuck in `true` state
    useInterval(updateIsInProgress, isInProgress ? 5000 : null);

    return isInProgress;
};
